import {ReactNode, useEffect} from "react"
import {Box, Chip, Divider, Stack, Typography, useTheme} from "@mui/joy"
import {Link} from "react-router-dom"
import {
    BarChart3,
    Brain,
    Check,
    Clock,
    Gift,
    Globe2,
    History as HistoryIcon,
    Search,
    Sparkles,
    Target,
    TrendingUp,
    Wallet
} from "lucide-react"
import {useTranslation} from "react-i18next"
import Button from "../components/Button"
import Card from "../components/Card"
import FullLogo from "../components/Logo"
import LanguageSwitch from "../components/LanguageSwitch"
import {useAuth} from "../hooks/useAuth"
import {useAsync} from "../hooks/useAsyncResult"
import {getPlans} from "../services/api/plans"
import {activePalette, applyTransparency} from "../theme"
import {PLANS, formatAmount} from "../values/plans"
import {PlanOffer} from "../types/transaction"
import {SUPPORT_EMAIL} from "../constants"

/** What the pricing page reads as "unlimited": no one reaches it, and the check stays a comparison */
const UNLIMITED_DAILY_PREDICTIONS = 1_000_000

/** The plans as the bundle ships them, for a catalogue that could not be read */
const bundledOffers = (): PlanOffer[] => PLANS.map(plan => ({
    tier: plan.tier,
    name: plan.name,
    price: plan.price,
    currency: plan.currency,
    billingCycle: plan.billingCycle,
    dailyPredictionsLimit: plan.dailyPredictions ?? UNLIMITED_DAILY_PREDICTIONS,
    recommended: plan.recommended
}))

const Section = ({children, id}: { children: ReactNode, id?: string }) =>
    // Border box throughout: the gutter is taken out of the width rather than added to it
    <Stack id={id} component={"section"} alignItems={"center"} width={"100%"} boxSizing={"border-box"}
           paddingX={{xs: 2, sm: 3, md: 4}} paddingY={{xs: 5, md: 8}}>
        <Stack width={"100%"} maxWidth={"68rem"} gap={{xs: 3, md: 5}}>
            {children}
        </Stack>
    </Stack>

const SectionTitle = ({title, subtitle}: { title: string, subtitle?: string }) =>
    <Stack gap={1} alignItems={"center"} textAlign={"center"}>
        <Typography level={"h2"} fontSize={{xs: "1.6rem", md: "2.2rem"}}>{title}</Typography>
        {subtitle && <Typography level={"body-md"} maxWidth={"40rem"}>{subtitle}</Typography>}
    </Stack>

/**
 * The page the domain opens on.
 *
 * Everything the app itself does is behind an account, so this is the only screen that has to
 * explain the product rather than run it: it stands outside the app layout, with its own header
 * and footer, and every path through it leads to the analysis page or to the plans.
 */
const Landing = () => {
    const {t} = useTranslation()
    const palette = activePalette(useTheme())
    const auth = useAuth()
    const catalogue = useAsync(getPlans, false, false, true)

    useEffect(() => {
        catalogue.execute()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // The catalogue is edited from the dashboard, so the prices shown are the ones actually sold
    const plans = catalogue.data?.response?.length ? catalogue.data.response : bundledOffers()

    const features = [
        {icon: <Target/>, title: "landingFeatureProbabilitiesTitle", text: "landingFeatureProbabilitiesText"},
        {icon: <TrendingUp/>, title: "landingFeatureGoalsTitle", text: "landingFeatureGoalsText"},
        {icon: <BarChart3/>, title: "landingFeatureScoreTitle", text: "landingFeatureScoreText"},
        {icon: <Check/>, title: "landingFeatureBetsTitle", text: "landingFeatureBetsText"},
        {icon: <Brain/>, title: "landingFeatureReasoningTitle", text: "landingFeatureReasoningText"},
        {icon: <HistoryIcon/>, title: "landingFeatureHistoryTitle", text: "landingFeatureHistoryText"}
    ]

    const steps = [
        {icon: <Search/>, title: "landingHowStepOneTitle", text: "landingHowStepOneText"},
        {icon: <Sparkles/>, title: "landingHowStepTwoTitle", text: "landingHowStepTwoText"},
        {icon: <BarChart3/>, title: "landingHowStepThreeTitle", text: "landingHowStepThreeText"}
    ]

    const figures = [
        {icon: <Globe2 size={18}/>, label: "landingStatLeagues", value: "landingStatLeaguesValue"},
        {icon: <Clock size={18}/>, label: "landingStatSpeed", value: "landingStatSpeedValue"},
        {icon: <Wallet size={18}/>, label: "landingStatPrice", value: "landingStatPriceValue"}
    ]

    return <Stack width={"100%"} minHeight={"100dvh"} alignItems={"center"} sx={{backgroundColor: "#050e1c"}}>
        {/* Header */}
        <Stack component={"header"} width={"100%"} alignItems={"center"}
               sx={{
                   position: "sticky",
                   top: 0,
                   zIndex: 10,
                   backgroundColor: "rgba(5,14,28,0.9)",
                   backdropFilter: "blur(10px)",
                   borderBottom: "1px solid rgba(255,255,255,0.08)"
               }}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}
                   width={"100%"} maxWidth={"68rem"} boxSizing={"border-box"}
                   paddingX={{xs: 2, sm: 3, md: 4}} paddingY={1.5}>
                <Link to={"/"} style={{display: "flex"}} aria-label={"Score Vision"}>
                    <FullLogo width={120}/>
                </Link>

                {/* The in-page links need a wide screen to be worth their room */}
                <Stack direction={"row"} gap={3} display={{xs: "none", md: "flex"}}>
                    {[
                        {href: "#features", label: "landingNavFeatures"},
                        {href: "#how-it-works", label: "landingNavHowItWorks"},
                        {href: "#plans", label: "landingNavPricing"}
                    ].map(item => <Typography key={item.href} component={"a"} href={item.href} level={"body-sm"}
                                              sx={{textDecoration: "none", "&:hover": {color: "white"}}}>
                        {t(item.label)}
                    </Typography>)}
                </Stack>

                <Stack direction={"row"} gap={1} alignItems={"center"}>
                    <Box display={{xs: "none", sm: "block"}}><LanguageSwitch/></Box>
                    {/* Signed out, the analysis page asks for the account itself */}
                    <Link to={"/analyze"} style={{textDecoration: "none"}}>
                        <Button size={"sm"}>{auth.user ? t("landingOpenApp") : t("signIn")}</Button>
                    </Link>
                </Stack>
            </Stack>
        </Stack>

        {/* Hero */}
        <Stack component={"section"} width={"100%"} alignItems={"center"} boxSizing={"border-box"}
               paddingX={{xs: 2, sm: 3, md: 4}} paddingY={{xs: 6, md: 10}}
               sx={{
                   backgroundImage: `radial-gradient(circle at 20% 0%, ${applyTransparency(palette.primary["500"], 0.35)}, transparent 55%),
                                     radial-gradient(circle at 85% 20%, ${applyTransparency("#2DDF82", 0.18)}, transparent 45%)`
               }}>
            <Stack maxWidth={"46rem"} gap={2.5} alignItems={"center"} textAlign={"center"}>
                <Typography level={"body-xs"} textColor={"primary.700"} letterSpacing={"0.2em"}>
                    {t("landingHeroEyebrow")}
                </Typography>
                <Typography level={"h1"} fontSize={{xs: "2.1rem", sm: "3rem", md: "3.6rem"}} lineHeight={1.1}>
                    {t("landingHeroTitle")}
                </Typography>
                <Typography level={"body-lg"} fontSize={{xs: "1rem", md: "1.1rem"}}>
                    {t("landingHeroSubtitle")}
                </Typography>

                <Stack direction={{xs: "column", sm: "row"}} gap={1.5} width={{xs: "100%", sm: "auto"}}>
                    <Link to={"/analyze"} style={{textDecoration: "none"}}>
                        <Button size={"lg"} startDecorator={<Sparkles size={18}/>} sx={{width: "100%"}}>
                            {t("landingHeroPrimary")}
                        </Button>
                    </Link>
                    <Link to={"/pricing"} style={{textDecoration: "none"}}>
                        {/* The shared button paints its label for the green fill, which an outline does not have */}
                        <Button size={"lg"} variant={"outlined"}
                                sx={{
                                    width: "100%",
                                    background: "transparent",
                                    color: "white",
                                    borderColor: "rgba(255,255,255,0.4)",
                                    "&:hover": {background: "rgba(255,255,255,0.08)", borderColor: "white"}
                                }}>
                            {t("landingHeroSecondary")}
                        </Button>
                    </Link>
                </Stack>

                <Typography level={"body-xs"}>{t("landingHeroNotice")}</Typography>
            </Stack>

            {/* Figures */}
            <Box display={"grid"} gap={{xs: 1.5, md: 2}} width={"100%"} maxWidth={"52rem"} marginTop={{xs: 5, md: 7}}
                 gridTemplateColumns={{xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))"}}>
                {figures.map(figure => <Card key={figure.label} sx={{gap: 0.5, alignItems: "center"}}>
                    <Typography level={"body-xs"} startDecorator={figure.icon}>{t(figure.label)}</Typography>
                    <Typography level={"title-lg"} textAlign={"center"}>{t(figure.value)}</Typography>
                </Card>)}
            </Box>
        </Stack>

        {/* Features */}
        <Section id={"features"}>
            <SectionTitle title={t("landingFeaturesTitle")} subtitle={t("landingFeaturesSubtitle")}/>

            <Box display={"grid"} gap={2}
                 gridTemplateColumns={{xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))"}}>
                {features.map(feature => <Card key={feature.title} sx={{gap: 1.5}}>
                    <Stack alignItems={"center"} justifyContent={"center"}
                           sx={{
                               width: 44,
                               height: 44,
                               borderRadius: "12px",
                               color: palette.primary["700"],
                               background: applyTransparency(palette.primary["500"], 0.15),
                               border: `1px solid ${applyTransparency(palette.primary["600"], 0.3)}`
                           }}>
                        {feature.icon}
                    </Stack>
                    <Typography level={"title-md"}>{t(feature.title)}</Typography>
                    <Typography level={"body-sm"}>{t(feature.text)}</Typography>
                </Card>)}
            </Box>
        </Section>

        {/* How it works */}
        <Section id={"how-it-works"}>
            <SectionTitle title={t("landingHowTitle")}/>

            <Box display={"grid"} gap={2}
                 gridTemplateColumns={{xs: "repeat(1, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))"}}>
                {steps.map((step, index) => <Card key={step.title} sx={{gap: 1.5}}>
                    <Stack direction={"row"} alignItems={"center"} gap={1.5}>
                        <Stack alignItems={"center"} justifyContent={"center"}
                               sx={{
                                   width: 36,
                                   height: 36,
                                   borderRadius: "50%",
                                   flexShrink: 0,
                                   color: "#050e1c",
                                   fontWeight: 700,
                                   background: `linear-gradient(45deg, ${palette.primary["600"]}, #2DDF82)`
                               }}>
                            {index + 1}
                        </Stack>
                        <Typography level={"title-md"} startDecorator={step.icon}>{t(step.title)}</Typography>
                    </Stack>
                    <Typography level={"body-sm"}>{t(step.text)}</Typography>
                </Card>)}
            </Box>
        </Section>

        {/* Plans */}
        <Section id={"plans"}>
            <SectionTitle title={t("landingPricingTitle")} subtitle={t("landingPricingSubtitle")}/>

            <Box display={"grid"} gap={2}
                 gridTemplateColumns={{xs: "repeat(1, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))"}}>
                {plans.map(plan => <Card key={plan.tier}
                                         sx={{
                                             gap: 1,
                                             position: "relative",
                                             overflow: "visible",
                                             borderColor: plan.recommended
                                                 ? palette.primary["600"]
                                                 : "rgba(255,255,255,0.1)",
                                             borderWidth: plan.recommended ? 2 : 1,
                                             background: plan.recommended
                                                 ? applyTransparency(palette.primary["500"], 0.12)
                                                 : "rgba(255,255,255,0.05)"
                                         }}>
                    {plan.recommended && <Chip size={"sm"} variant={"solid"} color={"primary"}
                                               sx={{position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)"}}>
                        {t("recommendedBadge")}
                    </Chip>}

                    <Typography level={"title-lg"}>{plan.name}</Typography>
                    <Stack direction={"row"} alignItems={"baseline"} gap={1} flexWrap={"wrap"}>
                        <Typography level={"h2"} fontSize={{xs: "1.8rem", md: "2rem"}}>
                            {formatAmount(plan.price)}
                        </Typography>
                        <Typography level={"body-sm"}>{t("currencyLabel")}</Typography>
                    </Stack>
                    <Typography level={"body-sm"} startDecorator={<Check size={16} style={{color: palette.primary["700"]}}/>}>
                        {plan.dailyPredictionsLimit >= UNLIMITED_DAILY_PREDICTIONS
                            ? t("landingPricingUnlimited")
                            : t("landingPricingPerDay", {count: plan.dailyPredictionsLimit})}
                    </Typography>
                </Card>)}
            </Box>

            <Stack alignItems={"center"}>
                <Link to={"/pricing"} style={{textDecoration: "none"}}>
                    <Button variant={"soft"} sx={{background: "rgba(255,255,255,0.08)", color: "white"}}>
                        {t("landingPricingCta")}
                    </Button>
                </Link>
            </Stack>
        </Section>

        {/* Referral */}
        <Section>
            <Card sx={{
                gap: 1.5,
                alignItems: "center",
                textAlign: "center",
                background: applyTransparency(palette.primary["500"], 0.12),
                borderColor: applyTransparency(palette.primary["600"], 0.35)
            }}>
                <Gift style={{color: "#2DDF82"}}/>
                <Typography level={"title-lg"}>{t("landingReferralTitle")}</Typography>
                <Typography level={"body-sm"} maxWidth={"38rem"}>{t("landingReferralText")}</Typography>
            </Card>
        </Section>

        {/* Closing call to action */}
        <Section>
            <Stack gap={2} alignItems={"center"} textAlign={"center"}>
                <Typography level={"h2"} fontSize={{xs: "1.6rem", md: "2.2rem"}}>{t("landingCtaTitle")}</Typography>
                <Typography level={"body-md"} maxWidth={"34rem"}>{t("landingCtaText")}</Typography>
                <Link to={"/analyze"} style={{textDecoration: "none"}}>
                    <Button size={"lg"} startDecorator={<Sparkles size={18}/>}>{t("landingCtaButton")}</Button>
                </Link>
            </Stack>
        </Section>

        {/* Footer */}
        <Stack component={"footer"} width={"100%"} alignItems={"center"} boxSizing={"border-box"}
               paddingX={{xs: 2, sm: 3, md: 4}} paddingY={{xs: 4, md: 6}}
               sx={{borderTop: "1px solid rgba(255,255,255,0.08)"}}>
            <Stack width={"100%"} maxWidth={"68rem"} gap={3}>
                <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} gap={3}>
                    <Stack gap={1} maxWidth={"22rem"}>
                        <FullLogo width={130}/>
                        <Typography level={"body-sm"}>{t("landingFooterTagline")}</Typography>
                        <Box paddingTop={1} width={"8rem"}><LanguageSwitch/></Box>
                    </Stack>

                    <Stack direction={"row"} gap={{xs: 4, sm: 6}} flexWrap={"wrap"}>
                        <Stack gap={1}>
                            <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
                                {t("landingFooterProduct")}
                            </Typography>
                            {[
                                {to: "/analyze", label: "navMatches"},
                                {to: "/history", label: "navHistory"},
                                {to: "/pricing", label: "navPricing"}
                            ].map(item => <Link key={item.to} to={item.to} style={{textDecoration: "none"}}>
                                <Typography level={"body-sm"} sx={{"&:hover": {color: "white"}}}>
                                    {t(item.label)}
                                </Typography>
                            </Link>)}
                        </Stack>

                        <Stack gap={1}>
                            <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
                                {t("landingFooterSupport")}
                            </Typography>
                            <Typography level={"body-sm"} component={"a"} href={`mailto:${SUPPORT_EMAIL}`}
                                        sx={{textDecoration: "none", "&:hover": {color: "white"}}}>
                                {t("landingFooterContact")}
                            </Typography>
                            <Typography level={"body-sm"} component={"a"} href={`mailto:${SUPPORT_EMAIL}`}
                                        sx={{textDecoration: "none", overflowWrap: "anywhere"}}>
                                {SUPPORT_EMAIL}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>

                <Divider/>

                <Stack gap={1}>
                    <Typography level={"body-xs"}>{t("landingFooterResponsible")}</Typography>
                    <Typography level={"body-xs"}>{t("landingFooterRights", {year: new Date().getFullYear()})}</Typography>
                </Stack>
            </Stack>
        </Stack>
    </Stack>
}

export default Landing
